import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Check, ShieldCheck, Sparkles } from 'lucide-react';
interface UpsellPopupProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
const ORIGINAL_TOTAL = 30; // 14 + 9 + 7
const BUMPS_TOTAL = 16; // 9 + 7
const DISCOUNTED_BUMPS = BUMPS_TOTAL * 0.7; // 11.20
const BUNDLE_PRICE = 14 + DISCOUNTED_BUMPS; // 25.20
const fmt = (n: number) => `$${n.toFixed(2)} USD`;
export function UpsellPopup({
  isOpen,
  onAccept,
  onDecline,
  onClose
}: UpsellPopupProps) {
  // Lock body scroll + escape to decline
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-388">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} className="fixed inset-0 z-[110] flex items-center justify-center p-4" aria-modal="true" role="dialog" data-id="element-389">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-charcoal/75 backdrop-blur-sm" onClick={onClose} aria-hidden="true" data-id="element-390" />

          {/* Panel */}
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="relative bg-cream rounded-3xl shadow-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto" data-id="element-391">
            {/* Close */}
            <button type="button" aria-label="Cerrar" onClick={onClose} className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-cream-dark hover:bg-sage-100 text-charcoal flex items-center justify-center transition-colors" data-id="element-392">
              <X size={18} data-id="element-393" />
            </button>

            {/* Header banner */}
            <div className="bg-sage-800 text-cream px-6 md:px-8 pt-8 pb-6 text-center rounded-t-3xl" data-id="element-394">
              <span className="inline-block bg-yellow-400 text-charcoal text-[11px] md:text-xs font-bold px-3 py-1 rounded-full tracking-widest uppercase mb-4 shadow-md" data-id="element-395">
                ⚡ Última oportunidad
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-2" data-id="element-396">
                ⚠️ ¡Espera! Antes de salir...
              </h2>
              <p className="text-sage-100/90 text-sm md:text-base" data-id="element-397">
                Tenemos una oferta que solo verás una vez en esta pantalla.
              </p>
            </div>

            {/* Body */}
            <div className="px-6 md:px-8 py-6" data-id="element-398">
              {/* Flashing discount badge */}
              <div className="flex justify-center mb-5" data-id="element-399">
                <div className="flash-badge bg-yellow-300 text-charcoal font-bold px-5 py-2 rounded-full text-sm md:text-base tracking-wider uppercase shadow-md flex items-center gap-2" data-id="element-400">
                  <Sparkles size={16} data-id="element-401" />
                  30% OFF en los Bonos Extra
                </div>
              </div>

              {/* Main copy */}
              <p className="text-center text-charcoal text-base md:text-lg leading-relaxed mb-3 font-medium" data-id="element-402">
                Lleva el{' '}
                <strong className="text-sage-700" data-id="element-403">Pack Completo</strong> con
                todos los extras que aceleran tu Reset hormonal. Recupera tu
                energía, calma tus noches y vuelve a sentirte ligera, clara y en
                control.
              </p>
              <p className="text-center text-charcoal-light text-sm leading-relaxed mb-6" data-id="element-404">
                Incluye el programa completo + los 2 bonos premium con un
                descuento exclusivo que no volverás a ver.
              </p>

              {/* What's Included */}
              <div className="bg-cream-dark rounded-2xl p-5 mb-6" data-id="element-405">
                <p className="text-xs uppercase tracking-widest text-sage-700 font-bold mb-3 text-center" data-id="element-406">
                  Esto es lo que te llevas
                </p>
                <ul className="space-y-2.5" data-id="element-407">
                  {[{
                title: 'Menopause Reset',
                desc: 'Programa completo de 15 módulos'
              }, {
                title: '+ 3 Bonos exclusivos del programa',
                desc: 'Lista de compras, diario de síntomas y guía de suplementos'
              }, {
                title: '21 Desayunos Hormonales en 5 Minutos',
                desc: 'Recetario para mantener tu azúcar estable y darte energía'
              }, {
                title: 'Guía de Rescate Nocturno',
                desc: 'Técnicas para calmar el sistema nervioso y dormir profundo'
              }].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-408">
                      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-sage-700 flex items-center justify-center mt-0.5" data-id="element-409">
                        <Check size={12} className="text-cream" strokeWidth={3} data-id="element-410" />
                      </div>
                      <div className="text-sm leading-snug" data-id="element-411">
                        <p className="font-semibold text-charcoal" data-id="element-412">
                          {item.title}
                        </p>
                        <p className="text-charcoal-light text-xs mt-0.5" data-id="element-413">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </div>

              {/* Pricing block */}
              <div className="bg-white border-2 border-sage-200 rounded-2xl p-5 mb-5 text-center relative" data-id="element-414">
                <p className="text-xs text-charcoal-light line-through mb-1" data-id="element-415">
                  Precio total normal: {fmt(ORIGINAL_TOTAL)}
                </p>
                <p className="text-4xl md:text-5xl font-bold text-sage-700 tracking-tighter" data-id="element-416">
                  {fmt(BUNDLE_PRICE)}
                </p>
                <p className="text-xs md:text-sm font-semibold text-sage-700 uppercase tracking-widest mt-2" data-id="element-417">
                  Precio especial — Solo en esta pantalla
                </p>
                <div className="flex items-center justify-center gap-1.5 text-xs text-charcoal-light/70 mt-3 pt-3 border-t border-sage-100" data-id="element-418">
                  <Lock size={12} data-id="element-419" />
                  <span data-id="element-420">Pago seguro y encriptado</span>
                  <span className="mx-1" data-id="element-421">·</span>
                  <ShieldCheck size={12} data-id="element-422" />
                  <span data-id="element-423">Garantía 30 días</span>
                </div>
              </div>

              {/* Accept CTA */}
              <button type="button" onClick={onAccept} className="glow-cta w-full bg-sage-700 hover:bg-sage-800 text-cream font-bold text-base md:text-lg py-4 px-6 rounded-xl tracking-wide transition-colors mb-3 leading-tight" data-id="element-424">
                ¡SÍ! Quiero el Pack Completo por {fmt(BUNDLE_PRICE)}
              </button>

              {/* Decline CTA */}
              <button type="button" onClick={onDecline} className="w-full text-charcoal-light/70 hover:text-charcoal text-sm py-2 underline-offset-4 hover:underline transition-colors" data-id="element-425">
                No gracias, continuar sin los extras →
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}